import React, { useEffect, useState, useRef, useContext } from "react";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { PendoService } from "@services";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ClearIcon } from "@mui/x-date-pickers";
import { IconButton } from "@mui/material";
import { SearchContext } from "../../../contexts/Search";

const PatientSearchCoverage = ({ channels, OnInsuranceChange, handleZipSearch, handleStateSearch, onGeographicalValueChange, searchCoverageClick, appliedConfiguration, onChangeBenefitType }) => {
  const { productId, productName, indicationName } = useContext(SearchContext);
  const [selectedInsuranceType, setInsuranceType] = useState(null);
  const [isDisableSearch, setDisableSearch] = useState(true);
  const [selectedZipCode, setSelectedZipCode] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [insuranceTypeErrorMessage, setInsuranceTypeErrorMessage] = useState("");
  const [geographyErrorMessage, setGeographyErrorMessage] = useState("");
  const [geographySearchTitle, setGeographySearchTitle] = useState("");
  const [zipCodeSearchOnly, setZipCodeSearchOnly] = useState(false);
  const [stateSearchOnly, setStateSearchOnly] = useState(false);
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [selectedBenefitType, setSelectedBenefitType] = useState("");
  const [states, setStates] = useState([]);
  const usZipCodeRegex = new RegExp(/^\d{5}(-\d{4})?(?!-)$/);
  const matchesMobileWidth = useMediaQuery("(max-width:1032px)");
  const geoSearchRef = useRef();
  const searchButtonRef = useRef();
  useEffect(() => {
    const cfConfig = appliedConfiguration?.coverageFinderConfiguration;
    const isZipCodeSearchOnly = cfConfig?.geographyType?.toLowerCase() == "zip code only";
    const isStateSearchOnly = cfConfig?.geographyType?.toLowerCase() == "state only";
    if (appliedConfiguration?.coverageFinderConfiguration?.benefitType?.toLowerCase() === "both-medical(default)") {
      setBenefitTypes(["Medical", "Pharmacy"]);
      setSelectedBenefitType("Medical");
    } else if (appliedConfiguration?.coverageFinderConfiguration?.benefitType?.toLowerCase() === "both-pharmacy(default)") {
      setBenefitTypes(["Pharmacy", "Medical"]);
      setSelectedBenefitType("Pharmacy");
    } else {
      setBenefitTypes([appliedConfiguration?.coverageFinderConfiguration?.benefitType]);
      setSelectedBenefitType(appliedConfiguration?.coverageFinderConfiguration?.benefitType);
    }
    setStateSearchOnly(isStateSearchOnly);
    setZipCodeSearchOnly(isZipCodeSearchOnly);
    setGeographySearchTitle(isZipCodeSearchOnly ? "ZIP Code:" : (isStateSearchOnly ? "State:" : "ZIP Code or State:"));
    if (!isZipCodeSearchOnly) {
      let states = appliedConfiguration?.states?.sort((a, b) =>
        a.name.toUpperCase() < b.name.toUpperCase() ? -1 : a.name.toUpperCase() > b.name.toUpperCase() ? 1 : 0
      );
      setStates(states);
    }
  }, [appliedConfiguration])
  const OnBenefitTypeChange = (newValue) => {
    if (!newValue || newValue === "") {
      setSelectedBenefitType("");
      setDisableSearch(true);
    }
    else {
      if (selectedInsuranceType && (selectedTerritory || selectedZipCode)) {
        setDisableSearch(false);
      }
    }
    setSelectedBenefitType(newValue);
    if (geographyErrorMessage === "" && insuranceTypeErrorMessage === "") {
      onChangeBenefitType(newValue, isDisableSearch);
    }
  };
  const OnInsuranceValueChange = (newValue) => {
    if (!newValue || newValue === "") {
      setSelectedZipCode("");
      setSelectedTerritory("");
      setGeographyErrorMessage("");
      setDisableSearch(true);
    }
    else {
      if (selectedBenefitType && (selectedTerritory || selectedZipCode))
        setDisableSearch(false);
    }
    setInsuranceType(newValue);
    OnInsuranceChange(newValue);
  };

  const handleClear = () => {
    setSelectedZipCode("");
    setSelectedTerritory("");
    setGeographyErrorMessage("");
    handleZipSearch("");
  };

  const handleZipCodeSearch = (value) => {
    setSelectedZipCode(value ?? "");
    setSelectedTerritory(value ?? "");
    handleZipSearch(value ?? "");
    if (value && selectedBenefitType && selectedInsuranceType) {
      setDisableSearch(false)
    } else {
      setDisableSearch(true);
    }
    if (geoSearchRef.current && geoSearchRef.current.querySelector("input")) {
      geoSearchRef.current.querySelector("input").blur();
    }
  };

  const handleSelectedTerritory = (value) => {
    if (value?.trim() !== "" && usZipCodeRegex.test(value))
      handleZipCodeSearch(value);
    else {
      setSelectedTerritory(value ?? "");
      handleStateSearch(value ?? "");
    }
    if (value && selectedBenefitType && selectedInsuranceType) {
      setDisableSearch(false)
    } else {
      setDisableSearch(true);
    }
  }

  const handleKeyDown = (event) => {
    if (event.keyCode == 13 && event.target.value) {
      handleSelectedTerritory(event?.target?.value);
      triggerCoverageSearch();
      if (!isNaN(Number(event.target.value[0]))) {
        event.stopPropagation();
      }
    }
  };

  const triggerCoverageSearch = () => {

    if (searchButtonRef?.current) {
      searchButtonRef.current.focus();
    }
    const searchCriteriaValidityStatus = getSearchCriteriaValidityStatus();

    if (searchCriteriaValidityStatus && searchCriteriaValidityStatus.isSearchCriteriaValid) {
      setInsuranceTypeErrorMessage("");
      setGeographyErrorMessage("");
      trackSearchButtonClick();
      searchCoverageClick();
    }
    else {
      setValidationErrorMessage(searchCriteriaValidityStatus);
      onGeographicalValueChange();
    }
  }

  const getSearchCriteriaValidityStatus = () => {
    const isZipCodeValid = (zipCodeSearchOnly && selectedZipCode && selectedZipCode.trim() !== "" && usZipCodeRegex.test(selectedZipCode));
    const isTerritoryValid = stateSearchOnly ? selectedTerritory && states?.find((st) => st.name.toLowerCase() === selectedTerritory?.toLowerCase())
      : selectedTerritory && (states?.find((st) => st.name.toLowerCase() === selectedTerritory?.toLowerCase()) || (selectedTerritory.trim() !== "" && usZipCodeRegex.test(selectedTerritory)));
    return {
      isSearchCriteriaValid: selectedInsuranceType && (isZipCodeValid || isTerritoryValid),
      isInsuranceTypeValid: selectedInsuranceType,
      isZipCodeValid: isZipCodeValid,
      isTerritoryValid: isTerritoryValid
    }
  }

  const trackSearchButtonClick = () => {
    PendoService.getInstance().trackEvent("Search Button Click", "Searched Territory", selectedTerritory, productId, productName, indicationName);
    PendoService.getInstance().trackEvent("Search Button Click", "Insurance Type", selectedInsuranceType, productId, productName, indicationName);
  }

  const setValidationErrorMessage = (searchCriteriaValidityStatus) => {
    if (!searchCriteriaValidityStatus?.isInsuranceTypeValid)
      setInsuranceTypeErrorMessage("Please select valid Insurance Type.");

    if (zipCodeSearchOnly && !searchCriteriaValidityStatus?.isZipCodeValid) {
      setGeographyErrorMessage("Please enter a valid ZIP Code.");
      return;
    }

    if (stateSearchOnly && !searchCriteriaValidityStatus?.isTerritoryValid) {
      setGeographyErrorMessage("Please enter a valid State.");
      return;
    }

    if (!searchCriteriaValidityStatus?.isTerritoryValid)
      setGeographyErrorMessage("Please enter a valid ZIP Code or State.");
  }

  return (
    <div style={{ padding: "20px", marginTop: "20px", width: "100%" }} className="norstella-cf-SearchCoverage">
      <div className="norstella-cf-flexAlignResponsive" part="cfDivSearch">
        {/* Insurance Component */}
        <div className={`norstella-cf-PatientColDiv norstella-cf-flexAlignCenterRow norstella-cf-bottomMargin ${matchesMobileWidth ? "" : "norstella-cf-searchComponents"}`} part="cfInsuranceTypeDiv">
          <div className="norstella-cf-div-col">
            <div className="norstella-cf-searchLabel norstella-cf-bottomMargin norstella-cf-InsuranceType"> Insurance Type:</div>
            <Autocomplete
              id="insuranceSelect"
              className="norstella-cf-patientInsuranceType"
              part="cfSearchText"
              disablePortal
              blurOnSelect
              handleHomeEndKeys
              options={channels}
              value={selectedInsuranceType}
              onChange={(event, newValue) => OnInsuranceValueChange(newValue)}
              renderInput={(params) => (
                <TextField {...params}
                  className="norstella-cf-txtSearchText"
                  placeholder="Select Insurance Type"
                  InputProps={{
                    ...params.InputProps
                  }}
                />)} />
            <div className="norstella-cf-message" part="cfMessage">
              {insuranceTypeErrorMessage}
            </div>
          </div>
        </div>
        {/* Benefit Type Component */}
        {benefitTypes?.length > 1 && (
          <div className={`norstella-cf-PatientColDiv norstella-cf-flexAlignCenterRow norstella-cf-bottomMargin norstella-cf-leftMargin ${matchesMobileWidth ? "" : "norstella-cf-benefitTypeComponents"}`} part="cfBenefitTypeDiv">
            <div className="norstella-cf-div-col">
              <div part="cfBenefitTypeLabel" className="norstella-cf-searchLabel norstella-cf-bottomMargin norstella-cf-BenefityType"> Benefit Type:</div>
              <Autocomplete
                id="benefiType"
                className="norstella-cf-patientBenefitType"
                part="cfPatientBenefitTYpe"
                disablePortal
                blurOnSelect
                handleHomeEndKeys
                options={benefitTypes}
                value={selectedBenefitType}
                onChange={(event, newValue) => OnBenefitTypeChange(newValue)}
                renderInput={(params) => (
                  <TextField {...params}
                    className="norstella-cf-txtBenefitType"
                    placeholder="Select Benefit Type"
                    InputProps={{
                      ...params.InputProps
                    }}
                  />)} />
            </div>
          </div>
        )}

        {/* ZipCode Component */}
        <div className={`norstella-cf-PatientColDiv norstella-cf-flexAlignCenterRow norstella-cf-bottomMargin norstella-cf-leftMargin ${matchesMobileWidth ? "" : "norstella-cf-searchComponents"}`} part="cfZipCodeiv">
          <div className="norstella-cf-div-col">
            <div className="norstella-cf-searchLabel norstella-cf-bottomMargin norstella-cf-ZipCode"> {geographySearchTitle}</div>
            {(zipCodeSearchOnly
              ? (<Autocomplete
                id="patientGeoSearch"
                className="norstella-cf-patientGeoSearch"
                part="cfPatientGeoSearch"
                disablePortal
                freeSolo
                popupIcon={null}
                blurOnSelect
                handleHomeEndKeys
                options={[]}
                value={selectedZipCode}
                disabled={!selectedInsuranceType}
                onInputChange={(_, value) => handleZipCodeSearch(value)}
                onChange={(_, value) => {
                  if (!value)
                    handleClear();
                }}
                ref={geoSearchRef}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter ZIP Code"
                  />
                )}
              />)
              : (<Autocomplete
                id="patientGeoSearch"
                className="norstella-cf-patientGeoSearch"
                part="cfPatientGeoSearch"
                disablePortal
                freeSolo={!stateSearchOnly}
                {...(stateSearchOnly === false ? { popupIcon: null } : {})}
                blurOnSelect
                handleHomeEndKeys
                options={states}
                value={selectedTerritory}
                disabled={!selectedInsuranceType}
                onInputChange={(_, value) => handleSelectedTerritory(value)}
                onChange={(_, value) => {
                  if (!value)
                    handleClear();
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onKeyDown={handleKeyDown}
                    placeholder={stateSearchOnly ? "Select State" : "Enter ZIP Code or State"}
                    InputProps={{
                      ...params.InputProps,
                      // Conditionally add endAdornment only if stateSearchOnly is false
                      ...(stateSearchOnly === false
                        ? {
                          endAdornment: (
                            <IconButton
                              tabIndex={selectedTerritory?.length > 0 ? 0 : -1}
                              className="norstella-cf-autocomplete-custom-close"
                              aria-label="Clear button"
                              onClick={() => setSelectedTerritory("")}>
                              <ClearIcon />
                            </IconButton>
                          )
                        }
                        : {}),
                    }}
                  />
                )}
                getOptionLabel={(option) => option.name ?? option}
              />))
            }
            <div className="norstella-cf-message" part="cfMessage">
              {geographyErrorMessage}
            </div>
          </div>
        </div>

        {!matchesMobileWidth && (
          <Button
            disabled={isDisableSearch}
            variant="contained"
            className="norstella-cf-patientsearchButton"
            id="searchButton"
            onClick={triggerCoverageSearch}
            part="cfSearchButton"
            ref={searchButtonRef}
          >
            Search
          </Button>
        )}
      </div>
      {matchesMobileWidth && (
        <div className="norstella-cf-SearchCoverage">
          <Button
            disabled={isDisableSearch}
            variant="contained"
            className="norstella-cf-patientsearchButton"
            id="searchButton"
            onClick={triggerCoverageSearch}
            part="cfSearchButton"
            ref={searchButtonRef}
          >
            Search
          </Button>
        </div>
      )}
    </div>
  );
};

export default PatientSearchCoverage;
